import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErrorApi, api, consulta } from '../api/cliente';
import type {
  Aliado,
  Bandera,
  Caracteristica,
  GuardarInmueble,
  Inmueble,
  PrecioHistorico,
  TipoInmueble,
  TipoOperacion,
  Zona,
} from '../api/tipos';
import { useSesion } from '../auth/sesion';
import CrearZona from '../ui/CrearZona';
import Galeria from './Galeria';
import Mandatos from './Mandatos';

/*
 * MapLibre son 270 kB. Cargarlo con el resto del panel haría esperar a la
 * pantalla de entrada por un mapa que solo aparece en la ficha de un inmueble.
 */
const SelectorUbicacion = lazy(() => import('../ui/SelectorUbicacion'));

import {
  AreaTexto,
  Aviso,
  Campo,
  Cargando,
  Casilla,
  Encabezado,
  Etiqueta,
  Seleccion,
  Tarjeta,
  pesos,
} from '../ui/piezas';

/** Los campos que la API puede marcar como pendientes, con su nombre de pantalla. */
const NOMBRES_PENDIENTES: Record<string, string> = {
  descripcion: 'Descripción',
  direccionPublica: 'Dirección pública',
  areaConstruida: 'Área construida',
  areaLote: 'Área del lote',
  habitaciones: 'Habitaciones',
  banos: 'Baños',
};

interface Ciudad {
  id: string;
  nombre: string;
  departamento: string;
}

interface PosibleDuplicado {
  id: string;
  codigo: string;
  titulo: string;
  estadoPublicacion: string;
  agente: string | null;
  aliado: string | null;
  metros: number;
  motivo: string;
}

interface Sede {
  id: string;
  nombre: string;
}

const OPERACIONES: TipoOperacion[] = [
  'VENTA',
  'ARRIENDO',
  'ARRIENDO_OPCION_COMPRA',
  'PERMUTA',
  'ANTICRESIS',
];

/** «ARRIENDO_OPCION_COMPRA» → «arriendo opcion compra». */
function nombreOperacion(tipo: TipoOperacion): string {
  return tipo.replaceAll('_', ' ').toLowerCase();
}

interface OperacionForm {
  tipo: TipoOperacion;
  precio: string;
  precioPublico: boolean;
  negociable: boolean;
  aceptaPermuta: boolean;
  permutaNotas: string;
}

/** Una modalidad en blanco, con las banderas apagadas. */
function operacionVacia(tipo: TipoOperacion): OperacionForm {
  return {
    tipo,
    precio: '',
    precioPublico: true,
    negociable: false,
    aceptaPermuta: false,
    permutaNotas: '',
  };
}

/** Si la modalidad cobra por mes. Decide si hay condiciones de arriendo que pedir. */
const esDeArriendo = (tipo: TipoOperacion) =>
  tipo === 'ARRIENDO' || tipo === 'ARRIENDO_OPCION_COMPRA';

/**
 * Lo que el formulario no enseña, sacado de la ficha tal como llegó.
 *
 * <p>La API aplica el comando entero al guardar: lo que no viaja, lo pone a
 * nulo. El formulario mandaba treinta y un campos de sesenta y cuatro, así que
 * cada guardado borraba en silencio los otros treinta y tres —servicios,
 * normativa, predial, frente y fondo…—. Hoy eran siempre nulos porque ninguna
 * pantalla los llenaba; el día que alguna lo hiciera, el siguiente guardado
 * desde aquí los habría perdido.
 *
 * <p>Los bloques llegan agrupados y con otros nombres
 * (`servicios.agua` → `servicioAgua`), por eso la traducción es explícita.
 */
function loQueNoSeEdita(i: Inmueble | undefined): Partial<GuardarInmueble> {
  if (!i) return {};
  const n = (v: unknown) => (v === undefined || v === null || v === '' ? null : Number(v));
  const t = (v: unknown) => (v === undefined || v === null ? null : String(v));
  const b = (v: unknown) => (v === undefined || v === null ? null : Boolean(v));
  return {
    agenteId: i.agenteId,
    destacado: i.destacado,
    areaBalconTerraza: n(i.areas.balconTerraza),
    frenteM: n(i.areas.frente),
    fondoM: n(i.areas.fondo),
    garajesCubiertos: n(i.distribucion.garajesCubiertos),
    pisosTotales: n(i.distribucion.pisosTotales),
    ascensores: n(i.distribucion.ascensores),
    estadoConstruccion: t(i.construccion.estado),
    administracionIncluida: b(i.costos.administracionIncluida),
    impuestoPredialAnual: n(i.costos.predialAnual),
    cedulaCatastral: t(i.legal.catastral),
    usoSuelo: t(i.normativa.usoSuelo),
    indiceOcupacion: n(i.normativa.indiceOcupacion),
    indiceConstruccion: n(i.normativa.indiceConstruccion),
    alturaPermitidaPisos: n(i.normativa.alturaPisos),
    clasificacionPot: t(i.normativa.clasificacionPot),
    topografia: t(i.normativa.topografia),
    accesoVia: t(i.normativa.accesoVia),
    servicioAgua: b(i.servicios.agua),
    servicioEnergia: b(i.servicios.energia),
    servicioGas: b(i.servicios.gas),
    servicioAlcantarillado: b(i.servicios.alcantarillado),
    servicioInternet: b(i.servicios.internet),
    pozoSeptico: b(i.servicios.pozoSeptico),
    fuenteAguaNatural: b(i.servicios.fuenteAguaNatural),
  };
}

/** Lo que el formulario manda a la API. Solo lo que el asesor puede tocar. */
interface Borrador {
  tipoInmuebleId: string;
  sedeId: string;
  ciudadId: string;
  zonaId: string;
  aliadoId: string;
  contactoDirectoAliado: boolean;
  videoUrl: string;
  recorridoUrl: string;
  titulo: string;
  descripcion: string;
  direccion: string;
  direccionPublica: string;
  latitud: string;
  longitud: string;
  mostrarUbicacionExacta: boolean;
  radioCirculoM: string;
  areaConstruida: string;
  areaPrivada: string;
  areaLote: string;
  habitaciones: string;
  banos: string;
  mediosBanos: string;
  garajes: string;
  depositos: string;
  pisoUbicacion: string;
  estrato: string;
  anoConstruccion: string;
  administracion: string;
  matriculaInmobiliaria: string;
  amoblado: boolean;
  /** Vacío = sin indicar, que no es lo mismo que «no». */
  admiteMascotas: '' | 'si' | 'no';
  disponibleDesde: string;
  depositoMeses: string;
  permanenciaMinimaMeses: string;
  operaciones: OperacionForm[];
  caracteristicas: Record<string, string>;
}

const VACIO: Borrador = {
  tipoInmuebleId: '',
  sedeId: '',
  ciudadId: '',
  zonaId: '',
  aliadoId: '',
  contactoDirectoAliado: false,
  videoUrl: '',
  recorridoUrl: '',
  titulo: '',
  descripcion: '',
  direccion: '',
  direccionPublica: '',
  latitud: '',
  longitud: '',
  mostrarUbicacionExacta: false,
  radioCirculoM: '150',
  areaConstruida: '',
  areaPrivada: '',
  areaLote: '',
  habitaciones: '',
  banos: '',
  mediosBanos: '',
  garajes: '',
  depositos: '',
  pisoUbicacion: '',
  estrato: '',
  anoConstruccion: '',
  administracion: '',
  matriculaInmobiliaria: '',
  amoblado: false,
  admiteMascotas: '',
  disponibleDesde: '',
  depositoMeses: '',
  permanenciaMinimaMeses: '',
  operaciones: [operacionVacia('VENTA')],
  caracteristicas: {},
};

/** Vacío se manda como nulo: un `""` en un número lo rechaza la validación. */
function numero(valor: string): number | null {
  if (valor.trim() === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

function texto(valor: string): string | null {
  return valor.trim() === '' ? null : valor.trim();
}

export default function InmuebleFicha() {
  const { id } = useParams();
  const navegar = useNavigate();
  const cache = useQueryClient();
  const { puede, usuario } = useSesion();
  const esNuevo = !id;

  const [forma, setForma] = useState<Borrador>(VACIO);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const ficha = useQuery({
    queryKey: ['inmueble', id],
    queryFn: () => api<Inmueble>(`/admin/inmuebles/${id}`),
    enabled: !esNuevo,
  });

  const tipos = useQuery({
    queryKey: ['tipos-inmueble'],
    queryFn: () => api<TipoInmueble[]>('/admin/catalogo/tipos-inmueble'),
  });
  /*
   * Las zonas dependen de la ciudad, asi que la consulta lleva la ciudad en su
   * clave: cambiar de ciudad trae otra lista, no la anterior filtrada mal.
   */
  /*
   * Posibles duplicados. Solo sobre un inmueble ya guardado: la busqueda es por
   * cercania y hace falta que el pin este en la base.
   */
  const duplicados = useQuery({
    queryKey: ['duplicados', id],
    queryFn: () => api<PosibleDuplicado[]>(`/admin/inmuebles/${id}/duplicados`),
    enabled: !esNuevo && !!id,
  });

  /*
   * El historial de precios. Se pide con la ficha y no al desplegarlo: son unas
   * pocas filas, y esconder tras un clic algo que cabe en tres lineas obliga a
   * descubrir que existe.
   */
  const historial = useQuery({
    queryKey: ['precios', id],
    queryFn: () => api<PrecioHistorico[]>(`/admin/inmuebles/${id}/precios`),
    enabled: !esNuevo && !!id,
  });

  const zonas = useQuery({
    queryKey: ['zonas', forma.ciudadId],
    queryFn: () => api<Zona[]>(`/public/geografia/zonas?ciudad=${forma.ciudadId}`),
    enabled: forma.ciudadId !== '',
  });

  const ciudades = useQuery({
    queryKey: ['ciudades'],
    queryFn: () => api<Ciudad[]>('/public/geografia/ciudades'),
  });
  const sedes = useQuery({
    queryKey: ['sedes'],
    queryFn: () => api<Sede[]>('/admin/sedes'),
  });
  const aliados = useQuery({
    queryKey: ['aliados', true],
    queryFn: () => api<Aliado[]>('/admin/aliados?activos=true'),
  });

  const tipo = useMemo(
    () => tipos.data?.find((t) => t.id === forma.tipoInmuebleId),
    [tipos.data, forma.tipoInmuebleId],
  );

  /**
   * Si el tipo elegido admite ese campo.
   *
   * Las banderas llegan anidadas en `aplica`. Se leían planas
   * (`tipo.aplicaHabitaciones`) y por tanto valían `undefined` siempre: el
   * formulario escondía área construida, habitaciones, baños, estrato, año,
   * piso y administración para cualquier tipo de inmueble, y después publicar
   * se rechazaba por esos mismos campos, que no había dónde rellenar.
   */
  const admite = (bandera: Bandera) => Boolean(tipo?.aplica?.[bandera]);

  // El catálogo se pide por tipo: es la API la que sabe qué aplica a un lote y
  // qué no, y así el formulario no tiene que repetir esa regla.
  const caracteristicas = useQuery({
    queryKey: ['caracteristicas', forma.tipoInmuebleId],
    queryFn: () =>
      api<Caracteristica[]>(
        `/admin/catalogo/caracteristicas${consulta({ tipo: forma.tipoInmuebleId })}`,
      ),
    enabled: Boolean(forma.tipoInmuebleId),
  });

  // Al abrir una ficha existente, el borrador se llena desde la API. Los
  // bloques llegan agrupados y sin los nulos, de ahí los `?? ''`.
  useEffect(() => {
    const i = ficha.data;
    if (!i) return;
    const bloque = (mapa: Record<string, unknown>, clave: string) =>
      mapa[clave] === undefined || mapa[clave] === null ? '' : String(mapa[clave]);

    setForma({
      tipoInmuebleId: i.tipoInmuebleId,
      sedeId: i.sedeId,
      ciudadId: i.ciudadId,
      zonaId: i.zonaId ?? '',
      aliadoId: i.aliadoId ?? '',
      contactoDirectoAliado: i.contactoDirectoAliado,
      videoUrl: i.videoUrl ?? '',
      recorridoUrl: i.recorridoUrl ?? '',
      titulo: i.titulo,
      descripcion: i.descripcion ?? '',
      direccion: i.direccion ?? '',
      direccionPublica: i.direccionPublica ?? '',
      latitud: i.latitud == null ? '' : String(i.latitud),
      longitud: i.longitud == null ? '' : String(i.longitud),
      mostrarUbicacionExacta: i.mostrarUbicacionExacta,
      radioCirculoM: String(i.radioCirculoM),
      areaConstruida: bloque(i.areas, 'construida'),
      areaPrivada: bloque(i.areas, 'privada'),
      areaLote: bloque(i.areas, 'lote'),
      habitaciones: bloque(i.distribucion, 'habitaciones'),
      banos: bloque(i.distribucion, 'banos'),
      mediosBanos: bloque(i.distribucion, 'mediosBanos'),
      garajes: bloque(i.distribucion, 'garajes'),
      depositos: bloque(i.distribucion, 'depositos'),
      pisoUbicacion: bloque(i.distribucion, 'pisoUbicacion'),
      estrato: bloque(i.construccion, 'estrato'),
      anoConstruccion: bloque(i.construccion, 'ano'),
      administracion: bloque(i.costos, 'administracion'),
      matriculaInmobiliaria: bloque(i.legal, 'matricula'),
      amoblado: i.arriendo.amoblado === true,
      admiteMascotas:
        i.arriendo.admiteMascotas === true ? 'si' : i.arriendo.admiteMascotas === false ? 'no' : '',
      disponibleDesde: bloque(i.arriendo, 'disponibleDesde'),
      depositoMeses: bloque(i.arriendo, 'depositoMeses'),
      permanenciaMinimaMeses: bloque(i.arriendo, 'permanenciaMinimaMeses'),
      /*
       * Sin filtro: la API ya devuelve solo las activas, y el filtro que había
       * miraba `o.activa`, un campo que el DTO no mandaba. Valía `undefined`,
       * así que descartaba todas las operaciones al abrir la ficha: se elegía
       * arriendo, se guardaba bien, y al recargar el tipo de operación había
       * desaparecido.
       *
       * Si no hay ninguna se deja una fila en blanco: un inmueble sin
       * operación no se puede publicar, y sin fila no hay dónde ponerla.
       */
      operaciones:
        i.operaciones.length > 0
          ? i.operaciones.map((o) => ({
              tipo: o.tipo,
              precio: o.precio === null ? '' : String(o.precio),
              precioPublico: o.precioPublico,
              negociable: o.negociable,
              aceptaPermuta: o.aceptaPermuta,
              permutaNotas: o.permutaNotas ?? '',
            }))
          : [operacionVacia('VENTA')],
      caracteristicas: Object.fromEntries(
        i.caracteristicas.map((c) => [c.id, c.valor ?? '']),
      ),
    });
  }, [ficha.data]);

  // Una sede por defecto en el alta: la de quien está cargando.
  useEffect(() => {
    if (esNuevo && usuario && !forma.sedeId) {
      setForma((f) => ({ ...f, sedeId: usuario.sedeId }));
    }
  }, [esNuevo, usuario, forma.sedeId]);

  /**
   * El valor de un campo que el tipo elegido no admite no se envía.
   *
   * <p>La API rechaza un campo que no aplica en vez de ignorarlo, y hace bien:
   * un dato que se guarda y no se muestra es un dato que alguien creyó haber
   * registrado. Pero el formulario esconde esos campos según el tipo, así que
   * al cambiar de casa a lote el valor viejo de habitaciones seguía viajando y
   * el guardado se rechazaba por un campo que ya no estaba en pantalla: sin
   * salida posible desde la interfaz. Aquí se descarta lo que el tipo nuevo no
   * admite, que es exactamente lo que el formulario ya dejó de mostrar.
   */
  const siAplica = (aplica: boolean | undefined, valor: string) =>
    aplica ? numero(valor) : null;

  const guardar = useMutation({
    mutationFn: async () => {
      /*
       * La API exige precio en toda operación. El formulario descartaba en
       * silencio las que no lo tenían: se elegía «arriendo», se guardaba, y la
       * operación no llegaba nunca —parecía que el tipo de operación se
       * perdiera solo—. Mejor decirlo y no guardar que guardar a medias.
       */
      const sinPrecio = forma.operaciones.filter((o) => o.precio.trim() === '');
      if (sinPrecio.length > 0) {
        const cuales = sinPrecio.map((o) => nombreOperacion(o.tipo)).join(', ');
        throw new Error(
          `Falta el precio de: ${cuales}. Ponlo, o quita esa modalidad con el botón «Quitar».`,
        );
      }

      const normativa = admite('normativaUrbana');
      const heredado = loQueNoSeEdita(esNuevo ? undefined : ficha.data);
      const cuerpo: GuardarInmueble = {
        ...heredado,
        // La API rechaza un dato que el tipo no admite. Si alguien cambia el
        // tipo, lo heredado de normativa ya no aplica y no hay en pantalla
        // donde quitarlo: se descarta aqui, igual que los demas campos.
        usoSuelo: normativa ? (heredado.usoSuelo ?? null) : null,
        indiceOcupacion: normativa ? (heredado.indiceOcupacion ?? null) : null,
        indiceConstruccion: normativa ? (heredado.indiceConstruccion ?? null) : null,
        tipoInmuebleId: forma.tipoInmuebleId,
        sedeId: forma.sedeId,
        ciudadId: forma.ciudadId,
        zonaId: texto(forma.zonaId),
        aliadoId: texto(forma.aliadoId),
        contactoDirectoAliado: forma.contactoDirectoAliado,
        videoUrl: texto(forma.videoUrl),
        recorridoUrl: texto(forma.recorridoUrl),
        titulo: forma.titulo,
        descripcion: texto(forma.descripcion),
        direccion: texto(forma.direccion),
        direccionPublica: texto(forma.direccionPublica),
        latitud: numero(forma.latitud),
        longitud: numero(forma.longitud),
        mostrarUbicacionExacta: forma.mostrarUbicacionExacta,
        radioCirculoM: numero(forma.radioCirculoM),
        areaConstruida: siAplica(admite('areaConstruida'), forma.areaConstruida),
        areaPrivada: siAplica(admite('areaConstruida'), forma.areaPrivada),
        areaLote: siAplica(admite('areaLote'), forma.areaLote),
        habitaciones: siAplica(admite('habitaciones'), forma.habitaciones),
        banos: siAplica(admite('banos'), forma.banos),
        mediosBanos: siAplica(admite('banos'), forma.mediosBanos),
        garajes: siAplica(admite('areaConstruida'), forma.garajes),
        depositos: siAplica(admite('areaConstruida'), forma.depositos),
        pisoUbicacion: siAplica(admite('pisoUbicacion'), forma.pisoUbicacion),
        estrato: siAplica(admite('estrato'), forma.estrato),
        anoConstruccion: siAplica(admite('anoConstruccion'), forma.anoConstruccion),
        // `administracionValor`, no `administracion`: con el nombre corto la
        // API no reconocía la clave y descartaba el importe en cada guardado
        // sin decir nada. Se leía bien --viene en `costos.administracion`--,
        // así que el valor parecía estar hasta que se recargaba la ficha.
        administracionValor: siAplica(admite('administracion'), forma.administracion),
        matriculaInmobiliaria: texto(forma.matriculaInmobiliaria),
        amoblado: forma.amoblado,
        admiteMascotas:
          forma.admiteMascotas === 'si' ? true : forma.admiteMascotas === 'no' ? false : null,
        disponibleDesde: texto(forma.disponibleDesde),
        depositoMeses: numero(forma.depositoMeses),
        permanenciaMinimaMeses: numero(forma.permanenciaMinimaMeses),
        operaciones: forma.operaciones.map((o) => ({
          tipo: o.tipo,
          precio: numero(o.precio),
          precioPublico: o.precioPublico,
          negociable: o.negociable,
          aceptaPermuta: o.aceptaPermuta,
          // Las notas solo tienen sentido si acepta permuta.
          permutaNotas: o.aceptaPermuta ? texto(o.permutaNotas) : null,
        })),
        caracteristicas: Object.entries(forma.caracteristicas).map(([id, valor]) => ({
          caracteristicaId: id,
          valor: texto(valor),
        })),
      };
      return esNuevo
        ? api<Inmueble>('/admin/inmuebles', { metodo: 'POST', cuerpo })
        : api<Inmueble>(`/admin/inmuebles/${id}`, { metodo: 'PUT', cuerpo });
    },
    onSuccess: (guardado) => {
      setError(null);
      setExito('Guardado.');
      void cache.invalidateQueries({ queryKey: ['inmuebles'] });
      if (esNuevo) navegar(`/inmuebles/${guardado.id}`, { replace: true });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const publicar = useMutation({
    mutationFn: (destino: string) =>
      api<Inmueble>(`/admin/inmuebles/${id}/publicacion/${destino}`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      setExito('Estado actualizado.');
      void cache.invalidateQueries({ queryKey: ['inmueble', id] });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  /**
   * Disponible, reservado, vendido, arrendado o retirado.
   *
   * <p>Es distinto de publicar. Publicar dice si la ficha se ve en el portal;
   * esto dice si el inmueble sigue en juego. Un vendido puede seguir publicado
   * un tiempo —enseña lo que se logró— y un retirado deja de admitir
   * solicitudes y visitas aunque nadie toque su publicación.
   */
  const cambiarEstadoComercial = useMutation({
    mutationFn: (destino: string) =>
      api<Inmueble>(`/admin/inmuebles/${id}/estado-comercial/${destino}`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      setExito('Estado comercial actualizado.');
      void cache.invalidateQueries({ queryKey: ['inmueble', id] });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const errorApi = error instanceof ErrorApi ? error : null;
  const campo = (nombre: string) => errorApi?.mensajeDe(nombre);
  const editable = puede(esNuevo ? 'inmueble:crear' : 'inmueble:editar');

  if (!esNuevo && ficha.isPending) return <Cargando />;

  /*
   * Si la ficha no cargó, no se pinta el formulario.
   *
   * Antes se pintaba vacío, y eso convertía un fallo de lectura en algo que
   * parecía culpa de quien lo estaba usando: el tipo de inmueble aparecía sin
   * elegir —y al editar no se puede cambiar—, así que al guardar salía «el tipo
   * no puede ser nulo» sobre un inmueble que sí tenía tipo. Peor todavía,
   * guardar así habría borrado los datos que no se alcanzaron a leer.
   */
  if (!esNuevo && ficha.isError) {
    return (
      <>
        <Encabezado titulo="No se pudo abrir el inmueble" />
        <Aviso error={ficha.error} />
        <p style={{ opacity: 0.8 }}>
          No se muestra el formulario a propósito: guardar con lo que no se pudo leer
          borraría datos.
        </p>
        <div style={{ height: 12 }} />
        <button onClick={() => void ficha.refetch()}>Intentar de nuevo</button>
      </>
    );
  }

  /*
   * La lista trae solo los aliados activos, que son los que se pueden asignar.
   * Pero si el de este inmueble se desactivo despues, no estaba en la lista: el
   * selector salia vacio, como si fuera inventario propio, aunque el inmueble
   * siguiera siendo suyo. Se añade el actual, marcado.
   */
  const opcionesDeAliado = (aliados.data ?? []).map((a) => ({ valor: a.id, texto: a.nombre }));
  if (
    ficha.data?.aliadoId &&
    !opcionesDeAliado.some((o) => o.valor === ficha.data!.aliadoId)
  ) {
    opcionesDeAliado.push({
      valor: ficha.data.aliadoId,
      texto: `${ficha.data.aliado ?? 'Aliado'} (desactivado)`,
    });
  }

  const activas = (caracteristicas.data ?? []).filter((c) => c.activa);
  const porGrupo = activas.reduce<Record<string, Caracteristica[]>>((acumulado, c) => {
    (acumulado[c.grupoNombre] ??= []).push(c);
    return acumulado;
  }, {});

  return (
    <>
      <Encabezado
        titulo={esNuevo ? 'Cargar inmueble' : (ficha.data?.codigo ?? '')}
        nota={esNuevo ? 'El código lo asigna la oficina al guardar.' : ficha.data?.titulo}
      >
        {!esNuevo && ficha.data && <Etiqueta>{ficha.data.estadoPublicacion}</Etiqueta>}
        {!esNuevo &&
          puede('inmueble:publicar') &&
          ficha.data?.transicionesPosibles.map((destino) => (
            <button
              key={destino}
              className="secundario"
              disabled={publicar.isPending}
              onClick={() => publicar.mutate(destino)}
            >
              {destino === 'PUBLICADO' ? 'Publicar' : destino.replaceAll('_', ' ').toLowerCase()}
            </button>
          ))}
      </Encabezado>

      <Aviso error={error} exito={exito} />

      {!esNuevo && (ficha.data?.pendientes ?? []).length > 0 && (
        <>
          {/*
            Lo que le falta a la ficha, permanente y al lado del formulario.

            Antes esto no existia: las mismas comprobaciones bloqueaban al
            pulsar «Publicar» y salian como un error, de golpe y sin haberlas
            podido ver antes. Ahora solo bloquean la coordenada y la operacion
            —sin ellas la ficha no funciona en el portal—, y el resto se lee
            aqui desde que se crea el inmueble. Informa y no impide: quien
            conoce el inmueble decide si sale asi.
          */}
          <Tarjeta titulo="Le falta esto para estar completa">
            <p style={{ opacity: 0.75, fontSize: 14, marginTop: 0 }}>
              Puedes publicarla igual. Esto es lo que se va a ver flojo en el portal.
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
              {ficha.data!.pendientes.map((p) => (
                <li key={p.campo}>
                  <strong>{NOMBRES_PENDIENTES[p.campo] ?? p.campo}</strong>: {p.mensaje}
                </li>
              ))}
            </ul>
          </Tarjeta>
          <div style={{ height: 16 }} />
        </>
      )}

      {(duplicados.data ?? []).length > 0 && (
        <>
          {/*
            Arriba y no al final: esto se tiene que leer antes de publicar, no
            despues de que un cliente pregunte por que el mismo piso aparece a
            dos precios. Informa y no bloquea —dos apartamentos iguales en el
            mismo edificio existen— pero lo pone donde no se puede ignorar.
          */}
          <Tarjeta titulo="Puede que este inmueble ya esté cargado">
            <p style={{ opacity: 0.75, fontSize: 14, marginTop: 0 }}>
              Hay {duplicados.data!.length === 1 ? 'otro inmueble' : 'otros inmuebles'} muy
              cerca, del mismo tipo y con las mismas medidas. Compruébalo antes de publicar.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {duplicados.data!.map((d) => (
                <div
                  key={d.id}
                  style={{ border: '1px solid var(--linea)', borderRadius: 8, padding: 10 }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Link to={`/inmuebles/${d.id}`}>{d.codigo}</Link>
                    <b>{d.titulo}</b>
                    <Etiqueta>{d.estadoPublicacion}</Etiqueta>
                  </div>
                  <small style={{ opacity: 0.75 }}>
                    {d.motivo}
                    {d.aliado ? ` · lo trae ${d.aliado}` : d.agente ? ` · de ${d.agente}` : ''}
                  </small>
                </div>
              ))}
            </div>
          </Tarjeta>
          <div style={{ height: 16 }} />
        </>
      )}

      <Tarjeta titulo="Identificación">
        <div className="rejilla">
          <Seleccion
            etiqueta="Tipo de inmueble"
            requerido
            valor={forma.tipoInmuebleId}
            // Cambiar el tipo cambia qué características se ofrecen, y las que
            // había dejarían de aplicar: la API las rechazaría una por una.
            // Se sueltan aquí en vez de dejar que el guardado falle.
            alCambiar={(v) =>
              setForma({
                ...forma,
                tipoInmuebleId: v,
                caracteristicas: v === forma.tipoInmuebleId ? forma.caracteristicas : {},
              })
            }
            vacio="Elige uno"
            error={campo('tipoInmuebleId')}
            ayuda={
              esNuevo
                ? 'Decide qué campos aplican y qué características se ofrecen'
                : 'Se puede cambiar. Al hacerlo se sueltan las características marcadas, porque las de un tipo no aplican a otro'
            }
            opciones={(tipos.data ?? []).map((t) => ({ valor: t.id, texto: t.nombre }))}
          />
          <Seleccion
            etiqueta="Oficina"
            requerido
            valor={forma.sedeId}
            alCambiar={(v) => setForma({ ...forma, sedeId: v })}
            error={campo('sedeId')}
            opciones={(sedes.data ?? []).map((s) => ({ valor: s.id, texto: s.nombre }))}
          />
          <Seleccion
            etiqueta="Ciudad"
            requerido
            valor={forma.ciudadId}
            alCambiar={(v) => setForma({ ...forma, ciudadId: v })}
            vacio="Elige una"
            error={campo('ciudadId')}
            opciones={(ciudades.data ?? []).map((c) => ({
              valor: c.id,
              texto: `${c.nombre} · ${c.departamento}`,
            }))}
          />
          <Seleccion
            etiqueta="Barrio o comuna"
            valor={forma.zonaId}
            alCambiar={(v) => setForma({ ...forma, zonaId: v })}
            vacio={forma.ciudadId === '' ? 'Elige antes la ciudad' : 'Sin especificar'}
            disabled={forma.ciudadId === ''}
            ayuda="Es lo que permite filtrar por barrio en el portal."
            opciones={(zonas.data ?? []).map((z) => ({
              valor: z.id,
              texto: z.tipo === 'COMUNA' ? z.nombre : `${z.nombre} (barrio)`,
            }))}
          />
        </div>

        {/* Crear el barrio aqui mismo. El momento en que se descubre que falta
            es este: alguien cargando un inmueble que no puede ubicar. */}
        {forma.ciudadId !== '' && puede('configuracion:editar') && (
          <CrearZona
            ciudadId={forma.ciudadId}
            zonas={zonas.data ?? []}
            alCrear={(z) => setForma({ ...forma, zonaId: z.id })}
          />
        )}

        <div style={{ height: 12 }} />
        <Campo
          etiqueta="Título"
          requerido
          valor={forma.titulo}
          alCambiar={(v) => setForma({ ...forma, titulo: v })}
          error={campo('titulo')}
          placeholder="Apartamento con vista a la montaña"
        />
        <div style={{ height: 12 }} />
        <AreaTexto
          etiqueta="Descripción"
          filas={5}
          valor={forma.descripcion}
          alCambiar={(v) => setForma({ ...forma, descripcion: v })}
          error={campo('descripcion')}
          ayuda="Obligatoria para publicar, con al menos 60 caracteres"
        />
      </Tarjeta>

      <Tarjeta titulo="Ubicación">
        <div className="rejilla">
          <Campo
            etiqueta="Dirección pública"
            valor={forma.direccionPublica}
            alCambiar={(v) => setForma({ ...forma, direccionPublica: v })}
            ayuda="La que ve el visitante: «Carrera 5 con Calle 37»"
            error={campo('direccionPublica')}
          />
          <Campo
            etiqueta="Dirección exacta"
            valor={forma.direccion}
            alCambiar={(v) => setForma({ ...forma, direccion: v })}
            ayuda="Interna. Nunca sale al portal"
            error={campo('direccion')}
          />
          <Campo
            etiqueta="Latitud"
            valor={forma.latitud}
            alCambiar={(v) => setForma({ ...forma, latitud: v })}
            error={campo('latitud')}
          />
          <Campo
            etiqueta="Longitud"
            valor={forma.longitud}
            alCambiar={(v) => setForma({ ...forma, longitud: v })}
            error={campo('longitud')}
          />
        </div>

        <div style={{ height: 12 }} />
        <Suspense fallback={<div style={{ height: 320 }}><Cargando /></div>}>
          <SelectorUbicacion
            latitud={forma.latitud}
            longitud={forma.longitud}
            editable={editable}
            alElegir={(latitud, longitud) =>
              setForma((previo) => ({ ...previo, latitud, longitud }))
            }
          />
        </Suspense>
        <div style={{ height: 12 }} />
        <Casilla
          etiqueta="Mostrar la ubicación exacta en el mapa"
          ayuda="Apagado, el portal no muestra el punto: muestra un círculo con el inmueble dentro. Es lo razonable en vivienda ocupada."
          valor={forma.mostrarUbicacionExacta}
          alCambiar={(v) => setForma({ ...forma, mostrarUbicacionExacta: v })}
        />

        {!forma.mostrarUbicacionExacta && (
          <div style={{ marginTop: 12, maxWidth: 320 }}>
            <Campo
              etiqueta="Radio del círculo (m)"
              valor={forma.radioCirculoM}
              alCambiar={(v) => setForma({ ...forma, radioCirculoM: v })}
              error={campo('radioCirculoM')}
              ayuda="Entre 50 y 1000. Es el círculo que ve el visitante; el inmueble queda dentro y cerca del centro."
            />
          </div>
        )}
      </Tarjeta>

      {tipo && (
        <Tarjeta titulo="Características del inmueble">
          {/*
            Cada campo se pinta solo si el tipo lo admite. Las banderas son las
            mismas que usa el backend para validar, así que el formulario no
            puede ofrecer algo que la API vaya a rechazar.
          */}
          <div className="rejilla">
            {admite('areaConstruida') && (
              <Campo
                etiqueta="Área construida (m²)"
                valor={forma.areaConstruida}
                alCambiar={(v) => setForma({ ...forma, areaConstruida: v })}
                error={campo('areaConstruida')}
              />
            )}
            {admite('areaConstruida') && (
              <Campo
                etiqueta="Área privada (m²)"
                valor={forma.areaPrivada}
                alCambiar={(v) => setForma({ ...forma, areaPrivada: v })}
                error={campo('areaPrivada')}
              />
            )}
            {admite('areaLote') && (
              <Campo
                etiqueta="Área del lote (m²)"
                valor={forma.areaLote}
                alCambiar={(v) => setForma({ ...forma, areaLote: v })}
                error={campo('areaLote')}
              />
            )}
            {admite('habitaciones') && (
              <Campo
                etiqueta="Habitaciones"
                valor={forma.habitaciones}
                alCambiar={(v) => setForma({ ...forma, habitaciones: v })}
                error={campo('habitaciones')}
              />
            )}
            {admite('banos') && (
              <Campo
                etiqueta="Baños"
                valor={forma.banos}
                alCambiar={(v) => setForma({ ...forma, banos: v })}
                error={campo('banos')}
              />
            )}
            {admite('banos') && (
              <Campo
                etiqueta="Medios baños"
                valor={forma.mediosBanos}
                alCambiar={(v) => setForma({ ...forma, mediosBanos: v })}
                error={campo('mediosBanos')}
              />
            )}
            {/* Con area construida y no con alcobas: una oficina o un local
                tienen parqueaderos, y antes no habia donde anotarlos. */}
            {admite('areaConstruida') && (
              <Campo
                etiqueta="Garajes"
                valor={forma.garajes}
                alCambiar={(v) => setForma({ ...forma, garajes: v })}
                error={campo('garajes')}
              />
            )}
            {admite('areaConstruida') && (
              <Campo
                etiqueta="Depósitos"
                valor={forma.depositos}
                alCambiar={(v) => setForma({ ...forma, depositos: v })}
                error={campo('depositos')}
              />
            )}
            {admite('pisoUbicacion') && (
              <Campo
                etiqueta="Piso"
                valor={forma.pisoUbicacion}
                alCambiar={(v) => setForma({ ...forma, pisoUbicacion: v })}
                error={campo('pisoUbicacion')}
              />
            )}
            {admite('estrato') && (
              <Campo
                etiqueta="Estrato"
                valor={forma.estrato}
                alCambiar={(v) => setForma({ ...forma, estrato: v })}
                error={campo('estrato')}
              />
            )}
            {admite('anoConstruccion') && (
              <Campo
                etiqueta="Año de construcción"
                valor={forma.anoConstruccion}
                alCambiar={(v) => setForma({ ...forma, anoConstruccion: v })}
                error={campo('anoConstruccion')}
              />
            )}
            {admite('administracion') && (
              <Campo
                etiqueta="Administración mensual"
                valor={forma.administracion}
                alCambiar={(v) => setForma({ ...forma, administracion: v })}
                error={campo('administracionValor')}
              />
            )}
          </div>
        </Tarjeta>
      )}

      <Tarjeta titulo="Operaciones">
        {forma.operaciones.map((operacion, indice) => (
          <div className="rejilla" key={indice} style={{ marginBottom: 10 }}>
            <Seleccion
              etiqueta="Modalidad"
              valor={operacion.tipo}
              alCambiar={(v) => {
                const copia = [...forma.operaciones];
                copia[indice] = { ...operacion, tipo: v as TipoOperacion };
                setForma({ ...forma, operaciones: copia });
              }}
              opciones={OPERACIONES.map((o) => ({ valor: o, texto: nombreOperacion(o) }))}
            />
            <Campo
              etiqueta="Precio"
              valor={operacion.precio}
              alCambiar={(v) => {
                const copia = [...forma.operaciones];
                copia[indice] = { ...operacion, precio: v };
                setForma({ ...forma, operaciones: copia });
              }}
              ayuda={operacion.precio ? pesos(Number(operacion.precio)) : undefined}
            />
            <div>
              <Casilla
                etiqueta="Mostrar el precio en el portal"
                valor={operacion.precioPublico}
                alCambiar={(v) => {
                  const copia = [...forma.operaciones];
                  copia[indice] = { ...operacion, precioPublico: v };
                  setForma({ ...forma, operaciones: copia });
                }}
              />
              {/* Antes no habia donde marcarlos, y cada guardado los ponia en
                  falso: el portal enseñaba «Negociable» y filtraba por «Acepta
                  permuta» sobre un dato que ningun inmueble podia tener. */}
              <Casilla
                etiqueta="Negociable"
                valor={operacion.negociable}
                alCambiar={(v) => {
                  const copia = [...forma.operaciones];
                  copia[indice] = { ...operacion, negociable: v };
                  setForma({ ...forma, operaciones: copia });
                }}
              />
              <Casilla
                etiqueta="Acepta permuta"
                valor={operacion.aceptaPermuta}
                alCambiar={(v) => {
                  const copia = [...forma.operaciones];
                  copia[indice] = { ...operacion, aceptaPermuta: v };
                  setForma({ ...forma, operaciones: copia });
                }}
              />
              {operacion.aceptaPermuta && (
                <Campo
                  etiqueta="Qué acepta en permuta"
                  ayuda="Por ejemplo: «carro hasta 60 millones» o «apartamento en Ibagué»"
                  valor={operacion.permutaNotas}
                  alCambiar={(v) => {
                    const copia = [...forma.operaciones];
                    copia[indice] = { ...operacion, permutaNotas: v };
                    setForma({ ...forma, operaciones: copia });
                  }}
                />
              )}
              {forma.operaciones.length > 1 && (
                <button
                  className="plano"
                  onClick={() =>
                    setForma({
                      ...forma,
                      operaciones: forma.operaciones.filter((_, i) => i !== indice),
                    })
                  }
                >
                  Quitar
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          className="secundario"
          onClick={() =>
            setForma({
              ...forma,
              operaciones: [
                ...forma.operaciones,
                operacionVacia('ARRIENDO'),
              ],
            })
          }
        >
          Agregar modalidad
        </button>
        <p className="campo__ayuda">
          Un mismo inmueble puede estar en venta y en arriendo a la vez, cada una con su precio.
        </p>

        {/*
          Como ha cambiado el precio.

          El precio se sobrescribe al guardar, asi que sin esto no queda rastro
          de a cuanto se puso ni de cuanto se ha bajado. Se anota solo, desde el
          primer precio: la ultima linea de cada modalidad es el precio de
          salida. Es informacion que solo se recoge hacia delante --lo que no se
          anote hoy no se reconstruye-- y sirve para dos cosas que se preguntan
          de verdad: «cuanto llevamos bajado» y «desde cuando esta en este
          precio».
        */}
        {(historial.data ?? []).length > 1 && (
          <div style={{ marginTop: 18, borderTop: '1px solid var(--linea)', paddingTop: 14 }}>
            <strong style={{ fontSize: 13 }}>Cómo ha cambiado el precio</strong>
            <ul style={{ margin: '10px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
              {historial.data!.map((linea, i) => {
                const bajo = linea.anterior != null && Number(linea.precio) < Number(linea.anterior);
                const subio = linea.anterior != null && Number(linea.precio) > Number(linea.anterior);
                return (
                  <li
                    key={`${linea.fecha}-${i}`}
                    style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 13 }}
                  >
                    <span style={{ opacity: 0.7, minWidth: 92 }}>
                      {new Date(linea.fecha).toLocaleDateString('es-CO', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                    <Etiqueta>{nombreOperacion(linea.tipo)}</Etiqueta>
                    <b>{pesos(Number(linea.precio))}</b>
                    {linea.anterior != null && (
                      <span style={{ opacity: 0.7 }}>
                        {bajo ? '▼ bajó desde ' : subio ? '▲ subió desde ' : 'desde '}
                        {pesos(Number(linea.anterior))}
                      </span>
                    )}
                    {linea.anterior == null && <span style={{ opacity: 0.7 }}>precio de salida</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Tarjeta>

      {/*
        Solo aparece si hay una modalidad de arriendo: a una venta no se le
        pregunta si admite mascotas.

        No existia. El portal ofrecia filtrar por «amoblado» y «admite
        mascotas» y cualquiera de los dos devolvia cero siempre, porque no
        habia pantalla donde marcarlo.
      */}
      {forma.operaciones.some((o) => esDeArriendo(o.tipo)) && (
        <>
          <div style={{ height: 16 }} />
          <Tarjeta titulo="Condiciones del arriendo">
            <div className="rejilla">
              <Seleccion
                etiqueta="Admite mascotas"
                valor={forma.admiteMascotas}
                alCambiar={(v) =>
                  setForma({ ...forma, admiteMascotas: v as Borrador['admiteMascotas'] })
                }
                vacio="Sin indicar"
                opciones={[
                  { valor: 'si', texto: 'Sí' },
                  { valor: 'no', texto: 'No' },
                ]}
              />
              <Campo
                etiqueta="Disponible desde"
                tipo="date"
                valor={forma.disponibleDesde}
                alCambiar={(v) => setForma({ ...forma, disponibleDesde: v })}
                error={campo('disponibleDesde')}
              />
              <Campo
                etiqueta="Depósito (meses de canon)"
                valor={forma.depositoMeses}
                alCambiar={(v) => setForma({ ...forma, depositoMeses: v })}
                error={campo('depositoMeses')}
              />
              <Campo
                etiqueta="Permanencia mínima (meses)"
                valor={forma.permanenciaMinimaMeses}
                alCambiar={(v) => setForma({ ...forma, permanenciaMinimaMeses: v })}
                error={campo('permanenciaMinimaMeses')}
              />
            </div>
            <Casilla
              etiqueta="Se arrienda amoblado"
              valor={forma.amoblado}
              alCambiar={(v) => setForma({ ...forma, amoblado: v })}
            />
          </Tarjeta>
        </>
      )}

      {Object.keys(porGrupo).length > 0 && (
        <Tarjeta titulo="Comodidades y detalles">
          {Object.entries(porGrupo).map(([grupo, lista]) => (
            <div key={grupo} style={{ marginBottom: 18 }}>
              <h3 style={{ marginBottom: 8 }}>{grupo}</h3>
              <div className="rejilla">
                {lista.map((c) => {
                  const valor = forma.caracteristicas[c.id];
                  const marcada = valor !== undefined;
                  const poner = (nuevo: string | undefined) => {
                    const copia = { ...forma.caracteristicas };
                    if (nuevo === undefined) delete copia[c.id];
                    else copia[c.id] = nuevo;
                    setForma({ ...forma, caracteristicas: copia });
                  };

                  if (c.tipoValor === 'BOOLEANO') {
                    return (
                      <Casilla
                        key={c.id}
                        etiqueta={c.nombre}
                        ayuda={c.ayuda ?? undefined}
                        valor={marcada}
                        alCambiar={(v) => poner(v ? '' : undefined)}
                      />
                    );
                  }
                  if (c.tipoValor === 'OPCION') {
                    return (
                      <Seleccion
                        key={c.id}
                        etiqueta={c.nombre}
                        ayuda={c.ayuda ?? undefined}
                        valor={valor ?? ''}
                        alCambiar={(v) => poner(v === '' ? undefined : v)}
                        vacio="Sin especificar"
                        opciones={c.opciones.map((o) => ({ valor: o, texto: o }))}
                      />
                    );
                  }
                  return (
                    <Campo
                      key={c.id}
                      etiqueta={c.unidad ? `${c.nombre} (${c.unidad})` : c.nombre}
                      ayuda={c.ayuda ?? undefined}
                      valor={valor ?? ''}
                      alCambiar={(v) => poner(v === '' ? undefined : v)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </Tarjeta>
      )}

      <Tarjeta titulo="Origen y multimedia">
        <div className="rejilla">
          <Seleccion
            etiqueta="Aliado"
            ayuda="Cuando el inmueble es de un comisionista externo"
            valor={forma.aliadoId}
            alCambiar={(v) =>
              setForma({
                ...forma,
                aliadoId: v,
                contactoDirectoAliado: v === '' ? false : forma.contactoDirectoAliado,
              })
            }
            vacio="Inventario propio"
            opciones={opcionesDeAliado}
          />
          <Campo
            etiqueta="Vídeo"
            ayuda="Enlace a YouTube o Vimeo; el vídeo no se aloja"
            valor={forma.videoUrl}
            alCambiar={(v) => setForma({ ...forma, videoUrl: v })}
            error={campo('videoUrl')}
          />
          <Campo
            etiqueta="Recorrido 3D"
            ayuda="Enlace de Realsee o Matterport. Se incrusta en la ficha del portal"
            valor={forma.recorridoUrl}
            alCambiar={(v) => setForma({ ...forma, recorridoUrl: v })}
            error={campo('recorridoUrl')}
          />
          <Campo
            etiqueta="Matrícula inmobiliaria"
            ayuda="Interna. No sale al portal"
            valor={forma.matriculaInmobiliaria}
            alCambiar={(v) => setForma({ ...forma, matriculaInmobiliaria: v })}
            error={campo('matriculaInmobiliaria')}
          />
        </div>
        {forma.aliadoId && (
          <>
            <div style={{ height: 12 }} />
            <Casilla
              etiqueta="El visitante contacta directo al aliado"
              ayuda="Apagado, la solicitud entra a la bandeja de Cordillera y no se pierde la relación con el cliente."
              valor={forma.contactoDirectoAliado}
              alCambiar={(v) => setForma({ ...forma, contactoDirectoAliado: v })}
            />
          </>
        )}
      </Tarjeta>

      <div className="fila fila--fin" style={{ marginTop: 18 }}>
        <button className="secundario" onClick={() => navegar('/inmuebles')}>
          Volver
        </button>
        <button disabled={!editable || guardar.isPending} onClick={() => guardar.mutate()}>
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>

      {!esNuevo && id && ficha.data && puede('inmueble:editar') && (
        <>
          <div style={{ height: 16 }} />
          <Tarjeta titulo="Estado comercial">
            <p style={{ opacity: 0.75, fontSize: 14, marginTop: 0 }}>
              Es distinto de publicar. Publicar dice si la ficha se ve en el portal;
              esto dice si el inmueble sigue en juego. Un vendido puede seguir publicado
              un tiempo —enseña lo que se logró— y un retirado deja de admitir solicitudes
              y visitas aunque nadie toque su publicación.
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Etiqueta>{ficha.data.estadoComercial}</Etiqueta>
              {(['DISPONIBLE', 'RESERVADO', 'VENDIDO', 'ARRENDADO', 'RETIRADO'] as const)
                .filter((destino) => destino !== ficha.data!.estadoComercial)
                .map((destino) => (
                  <button
                    key={destino}
                    className="plano"
                    disabled={cambiarEstadoComercial.isPending}
                    onClick={() => cambiarEstadoComercial.mutate(destino)}
                  >
                    {destino.toLowerCase()}
                  </button>
                ))}
            </div>
          </Tarjeta>
        </>
      )}

      {!esNuevo && id && forma.sedeId && (
        <>
          <Mandatos inmuebleId={id} sedeId={forma.sedeId} />
          <div style={{ height: 16 }} />
        </>
      )}
      {!esNuevo && id && <Galeria inmuebleId={id} />}

      {!esNuevo && id && (
        <Tarjeta titulo="Recorrido 360°">
          <p className="campo__ayuda" style={{ marginTop: 0 }}>
            Una foto 360° por espacio y los puntos de paso entre ellas. El visitante recorre el
            inmueble desde el portal sin moverse de su casa.
          </p>
          <Link className="secundario" to={`/inmuebles/${id}/recorrido`}>
            Armar el recorrido
          </Link>
        </Tarjeta>
      )}
    </>
  );
}
